import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';
import { motion } from 'framer-motion';
import AdBanner from '../AdPage/AdBanner';
// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const PersonalLoanEmiCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(8);
  const [loanTenure, setLoanTenure] = useState(5);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal * monthlyInterest * Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let currentYear = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: (currentMonth + i) % 12 + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === '' ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === '' ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === '' ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        label: 'EMI Breakdown',
        data: [loanAmount, totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
        {
            question: "What is a Personal Loan EMI?",
            answer: "An EMI (Equated Monthly Instalment) is the fixed monthly payment you make to repay your loan. It includes both the principal and interest components."
          },
          {
            question: "Can I use TheFinMaster’s calculator for any bank or NBFC?",
            answer: "Yes. It is a universal tool. You can input interest rates and terms from any lender to calculate EMI."
          },
          {
            question: "Will my EMI remain fixed throughout the loan tenure?",
            answer: "In most cases, yes—especially if you choose a fixed interest rate. If you opt for a floating rate, EMI may fluctuate."
          },
          {
            question: "How accurate is the EMI shown by the calculator?",
            answer: "TheFinMaster’s calculator uses the standard EMI formula, so it is extremely accurate. However, final EMIs may vary slightly due to lender-specific charges or processing fees."
          },
          {
            question: "Is it mandatory to use an EMI calculator before applying for a personal loan?",
            answer: "Not mandatory, but highly recommended. It helps ensure your loan is within your repayment capacity and avoids future defaults."
          },
          {
            question: "Does the calculator consider processing fees or prepayment charges?",
            answer: "No. It focuses on EMI, principal, and interest. You should check additional charges with your lender."
          },
          {
            question: "How do I reduce my EMI amount?",
            answer: "You can reduce EMI by increasing the loan tenure or negotiating a lower interest rate with your lender."
          },
          {
            question: "What is the maximum tenure allowed in the calculator?",
            answer: "Typically, you can calculate up to 5 years (60 months), but it supports longer terms if your lender permits."
          },
          {
            question: "What if I prepay my personal loan early?",
            answer: "Prepayments reduce your interest burden. Though not reflected in the EMI calculator, you can estimate savings by comparing with the amortization schedule."
          },
          {
            question: "Does using the EMI calculator affect my credit score?",
            answer: "No. It is a free tool and has no impact on your credit score or credit history."
          },
          {
            question: "How often can I use the calculator?",
            answer: "Unlimited usage—test different loan amounts, tenures, and rates to find your best-fit EMI."
          },
          {
            question: "Can I use this calculator on mobile?",
            answer: "Absolutely. TheFinMaster’s EMI calculator is fully mobile-optimized for ease of use on any device."
          },
          {
            question: "Does the calculator offer amortization schedules?",
            answer: "Yes. It generates a complete month-wise breakup of interest, principal, and outstanding balance."
          },
          {
            question: "Can I use it to plan for other types of loans too?",
            answer: "This tool is tailored for personal loans, but TheFinMaster also offers separate calculators for home loans, car loans, gold loans, and more."
          },
          {
            question: "Is this tool available in regional languages?",
            answer: "Currently, the calculator is available in English, but regional language support is in the works for broader accessibility."
          }
        ]
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
    const calculators = [
      {
        name: 'HDFC Personal Loan Calculator',
        path: '/financial-tools-calculators/hdfc-personal-loan-emi-calculator',
        image: '/assets/hdd.png',
      },
      {
        name: 'ICICI Personal Loan Calculator',
        path: '/financial-tools-calculators/icici-personal-loan-emi-calculator',
        image: '/assets/icc.png',
      },
      {
        name: 'Axis Personal Loan Calculator',
        path: '/financial-tools-calculators/axis-personal-loan-emi-calculator',
        image: '/assets/ax.png',
      },
      {
        name: 'Kotak Personal Loan Calculator',
        path: '/financial-tools-calculators/kotak-personal-loan-emi-calculator',
        image: '/assets/kot.png',
      },
      {
        name: 'IndusInd Personal Loan Calculator',
        path: '/financial-tools-calculators/indusind-personal-loan-emi-calculator',
        image: '/assets/ind121.png',
      },
      {
        name: 'Yes Bank Personal Loan Calculator',
        path: '/financial-tools-calculators/yes-bank-personal-loan-emi-calculator',
        image: '/assets/yes121.png',
      },
      {
        name: 'Federal Bank Personal Loan Calculator',
        path: '/financial-tools-calculators/federal-bank-personal-loan-emi-calculator',
        image: '/assets/fdd.png',
      },
      {
        name: 'IDFC Personal Loan Calculator',
        path: '/financial-tools-calculators/idfc-personal-loan-emi-calculator',
        image: '/assets/idd1.png',
      },
      {
        name: 'Citi Personal Loan Calculator',
        path: '/financial-tools-calculators/citi-personal-loan-emi-calculator',
        image: '/assets/citia.png',
      },
      {
        name: 'Standard Chartered Personal Loan Calculator',
        path: '/financial-tools-calculators/standard-chartered-personal-loan-emi-calculator',
        image: '/assets/std121.png',
      },
    ];
  return (
    <>
     <Helmet>
         <title>Personal Loan EMI Calculator – Monthly EMI & Interest</title>
        <meta  name='description' content='Calculate personal loan EMI and total interest payable with our accurate personal loan EMI calculator.'/>
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/personal-loan-emi-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Gold Loan EMI Calculator</span>
        </li> 
      
      </ol>
    </nav> */}
        <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
        <div className='py-4'>
    <div className="container mx-auto p-4 shadow-xl rounded-md">
      <h1 className="text-2xl font-bold mb-4">Personal Loan EMI Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Amount</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={loanAmount}
                onChange={handleLoanAmountChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {loanAmount.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (P.A.)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={interestRate}
                onChange={handleInterestRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{interestRate} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="15"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Tenure (Years)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="30"
                value={loanTenure}
                onChange={handleLoanTenureChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{loanTenure} Yr</span>
            </div>
            <input
              type="range"
              min="0"
              max="30"
              value={loanTenure}
              onChange={(e) => setLoanTenure(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateEMI}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
            <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
            <p>Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}</p>
            <p>Total Amount (Principal + Interest): ₹ {parseFloat(totalAmount).toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
        {amortizationSchedule.length > 0 && (
          <div className="flex flex-col space-y-4">
            {Array.from(new Set(amortizationSchedule.map(item => item.year))).map((year, idx) => (
              <div key={idx} className="mb-4 border rounded-md shadow-md">
                <div
                  className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${activeYear === year ? 'bg-blue-100' : ''}`}
                  onClick={() => handleYearToggle(year)}
                >
                  <span>{year}</span>
                  <span>{activeYear === year ? '-' : '+'}</span>
                </div>
                {activeYear === year && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-md shadow-md">
                      <thead>
                        <tr>
                          <th className="py-2 px-4 border-b">Month</th>
                          <th className="py-2 px-4 border-b">Principal Paid</th>
                          <th className="py-2 px-4 border-b">Interest Charged</th>
                          <th className="py-2 px-4 border-b">Total Payment</th>
                          <th className="py-2 px-4 border-b">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amortizationSchedule.filter(item => item.year === year).map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-2 px-4 border-b">{new Date(0, item.month - 1).toLocaleString('default', { month: 'short' })}</td>
                            <td className="py-2 px-4 border-b">₹ {item.principalPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.interestPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.totalPayment.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.balance.toFixed(2).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    <div className="p-4 sm:p-8 bg-white">
      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
      Personal Loan EMI Calculator 
      </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {calculators.map((calc, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.04 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={calc.path}
                    className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                  >
                    <img
                      src={calc.image}
                      alt={calc.name}
                      className="w-16 h-16 object-contain mb-2"
                    />
                    <span className="text-md font-medium text-gray-800 break-words leading-snug">
                      {calc.name}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
    </div>
     <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
     <div className="w-full md:w-2/3 p-4 mx-auto">
     <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Plan Your Personal Loan Wisely with TheFinMaster’s EMI Calculator
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Taking a personal loan is a major financial decision, and planning it well in advance can save you from future debt traps and financial stress. TheFinMaster’s Personal Loan EMI Calculator is your intelligent digital companion to help you plan your loan repayments with confidence. Whether you're borrowing for medical expenses, weddings, travel, or debt consolidation, this tool allows you to simulate various loan scenarios and assess your monthly liabilities instantly.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Rather than going through complex manual calculations or relying on vague approximations, the EMI calculator gives you real-time insights. It helps you adjust your loan amount, tenure, or interest rate to achieve a perfect balance between affordability and quick repayment. With clear breakdowns of principal and interest, TheFinMaster enables you to make fully informed borrowing decisions that align with your income and long-term goals.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Personal Loan EMI?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Personal Loan EMI (Equated Monthly Instalment) refers to the fixed monthly amount you pay to the lender to repay your loan over a specified tenure. This EMI includes both the principal repayment and the interest charged on the outstanding amount. It remains consistent throughout the repayment period in most cases unless you opt for a floating interest rate.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Because personal loans are unsecured—meaning they don’t require collateral—the interest rates can be slightly higher compared to secured loans. This makes it even more important to understand your EMI obligations in advance. With the right EMI plan, you can ensure timely repayments, avoid penalties, and maintain a healthy credit score.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use TheFinMaster’s Personal Loan EMI Calculator?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using TheFinMaster’s Personal Loan EMI Calculator provides more than just a quick calculation—it equips you with deeper financial clarity. Here's why it’s the go-to tool before taking a personal loan:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Instant EMI Calculation:</strong> No need to use complex formulas or spreadsheets. You can calculate your EMI instantly by entering just the loan amount, interest rate, and tenure.</li>
              <li><strong>Multiple Loan Scenarios:</strong> You can simulate different loan combinations—like increasing tenure or lowering the principal—to find a monthly EMI that suits your financial comfort zone.</li>
              <li><strong>Accurate Interest Projections:</strong> The calculator precisely shows your total interest outgo over the entire tenure, helping you assess the real cost of the loan.</li>
              <li><strong>Monthly Budget Planning:</strong> Knowing your fixed EMI beforehand allows you to prepare your monthly budget efficiently without financial strain.</li>
              <li><strong>Improves Loan Negotiation:</strong> With better knowledge of how interest and tenure affect EMI, you’re in a stronger position to negotiate terms with the lender.</li>
              <li><strong>Amortization Insight:</strong> It generates an amortization schedule that gives you a full breakdown of how your loan is repaid month-by-month, showing the split between principal and interest.</li>
              <li><strong>User-Friendly Interface:</strong> Even users without financial knowledge can use the tool easily—there’s no jargon, just simple and intuitive inputs.</li>
              <li><strong>Mobile Responsive & Secure:</strong> You can use it on-the-go without logging in or entering sensitive data, ensuring both convenience and privacy.</li>
              <li><strong>Free of Cost:</strong> Unlike other portals that may charge or limit usage, TheFinMaster’s calculator is completely free and unlimited.</li>
              <li><strong>Supports Financial Goals:</strong> Whether you want a low EMI or wish to close your loan faster, the calculator helps tailor your loan plan to match your financial aspirations.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the Personal Loan EMI Calculator Work?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              TheFinMaster’s Personal Loan EMI Calculator operates on the standard EMI calculation formula used across banks and NBFCs:
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              EMI = [P x R x (1+R)^N] / [(1+R)^N – 1]
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = Loan amount (principal)</li>
              <li>R = Monthly interest rate (Annual Rate ÷ 12 ÷ 100)</li>
              <li>N = Loan tenure in months</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Let’s say you want to borrow ₹5,00,000 for 3 years at an interest rate of 12% per annum.<br />
              Principal (P) = ₹5,00,000<br />
              Annual Interest Rate = 12%, so Monthly Rate (R) = 12/12/100 = 0.01<br />
              Tenure (N) = 3 years = 36 months<br />
              EMI = [500000 x 0.01 x (1+0.01)^36] / [(1+0.01)^36 – 1]<br />
              EMI ≈ ₹16,608
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Thus, you will pay ₹16,608 every month for 36 months.<br />
              The calculator performs this computation instantly and also shows the total interest outgo, total repayment amount, and an amortization schedule, giving you a full financial snapshot.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Amortization Schedule
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              An Amortization Schedule is a detailed table that illustrates how a loan is repaid over time through EMIs. Each EMI comprises a principal component (the original loan amount) and an interest component (the cost of borrowing). In the early months, the interest portion is higher and the principal is lower. Over time, as the outstanding balance reduces, the interest part shrinks, and the principal repayment increases.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This schedule is vital because it:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Helps you understand how much of your EMI is going toward reducing the loan vs paying interest.</li>
              <li>Provides a month-wise snapshot of your balance, helping you track repayments.</li>
              <li>Enables strategic decisions, like prepayments, by showing you interest-heavy months.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Below is the complete Amortization Schedule for a ₹5,00,000 loan, 12% interest p.a., for 36 months:
            </p>
            {/* <div className="overflow-x-auto">
              <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Month</th>
                    <th className="border px-4 py-2 text-left">EMI (₹)</th>
                    <th className="border px-4 py-2 text-left">Interest (₹)</th>
                    <th className="border px-4 py-2 text-left">Principal (₹)</th>
                    <th className="border px-4 py-2 text-left">Outstanding Balance (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">1</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">5,000</td>
                    <td className="border px-4 py-2">11,608</td>
                    <td className="border px-4 py-2">4,88,392</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">2</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">4,884</td>
                    <td className="border px-4 py-2">11,724</td>
                    <td className="border px-4 py-2">4,76,668</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">3</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">4,767</td>
                    <td className="border px-4 py-2">11,841</td>
                    <td className="border px-4 py-2">4,64,827</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">4</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">4,648</td>
                    <td className="border px-4 py-2">11,960</td>
                    <td className="border px-4 py-2">4,52,867</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">5</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">4,529</td>
                    <td className="border px-4 py-2">12,079</td>
                    <td className="border px-4 py-2">4,40,788</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">6</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">4,408</td>
                    <td className="border px-4 py-2">12,200</td>
                    <td className="border px-4 py-2">4,28,588</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">7</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">4,286</td>
                    <td className="border px-4 py-2">12,322</td>
                    <td className="border px-4 py-2">4,16,266</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">8</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">4,163</td>
                    <td className="border px-4 py-2">12,445</td>
                    <td className="border px-4 py-2">4,03,821</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">9</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">4,038</td>
                    <td className="border px-4 py-2">12,570</td>
                    <td className="border px-4 py-2">3,91,251</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">10</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">3,913</td>
                    <td className="border px-4 py-2">12,695</td>
                    <td className="border px-4 py-2">3,78,556</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">11</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">3,786</td>
                    <td className="border px-4 py-2">12,822</td>
                    <td className="border px-4 py-2">3,65,734</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">12</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">3,657</td>
                    <td className="border px-4 py-2">12,951</td>
                    <td className="border px-4 py-2">3,52,783</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">13</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">3,528</td>
                    <td className="border px-4 py-2">13,080</td>
                    <td className="border px-4 py-2">3,39,703</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">14</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">3,397</td>
                    <td className="border px-4 py-2">13,211</td>
                    <td className="border px-4 py-2">3,26,492</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">15</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">3,265</td>
                    <td className="border px-4 py-2">13,343</td>
                    <td className="border px-4 py-2">3,13,149</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">16</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">3,131</td>
                    <td className="border px-4 py-2">13,477</td>
                    <td className="border px-4 py-2">2,99,672</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">17</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">2,997</td>
                    <td className="border px-4 py-2">13,611</td>
                    <td className="border px-4 py-2">2,86,061</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">18</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">2,861</td>
                    <td className="border px-4 py-2">13,747</td>
                    <td className="border px-4 py-2">2,72,314</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">19</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">2,723</td>
                    <td className="border px-4 py-2">13,885</td>
                    <td className="border px-4 py-2">2,58,429</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">20</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">2,584</td>
                    <td className="border px-4 py-2">14,024</td>
                    <td className="border px-4 py-2">2,44,405</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">21</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">2,444</td>
                    <td className="border px-4 py-2">14,164</td>
                    <td className="border px-4 py-2">2,30,241</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">22</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">2,302</td>
                    <td className="border px-4 py-2">14,306</td>
                    <td className="border px-4 py-2">2,15,935</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">23</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">2,159</td>
                    <td className="border px-4 py-2">14,449</td>
                    <td className="border px-4 py-2">2,01,486</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">24</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">2,015</td>
                    <td className="border px-4 py-2">14,593</td>
                    <td className="border px-4 py-2">1,86,893</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">25</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">1,869</td>
                    <td className="border px-4 py-2">14,739</td>
                    <td className="border px-4 py-2">1,72,154</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">26</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">1,722</td>
                    <td className="border px-4 py-2">14,886</td>
                    <td className="border px-4 py-2">1,57,268</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">27</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">1,573</td>
                    <td className="border px-4 py-2">15,035</td>
                    <td className="border px-4 py-2">1,42,233</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">28</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">1,422</td>
                    <td className="border px-4 py-2">15,186</td>
                    <td className="border px-4 py-2">1,27,047</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">29</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">1,270</td>
                    <td className="border px-4 py-2">15,338</td>
                    <td className="border px-4 py-2">1,11,709</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">30</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">1,117</td>
                    <td className="border px-4 py-2">15,491</td>
                    <td className="border px-4 py-2">96,218</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">31</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">962</td>
                    <td className="border px-4 py-2">15,646</td>
                    <td className="border px-4 py-2">80,572</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">32</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">806</td>
                    <td className="border px-4 py-2">15,802</td>
                    <td className="border px-4 py-2">64,770</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">33</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">648</td>
                    <td className="border px-4 py-2">15,960</td>
                    <td className="border px-4 py-2">48,810</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">34</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">488</td>
                    <td className="border px-4 py-2">16,120</td>
                    <td className="border px-4 py-2">32,690</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">35</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">327</td>
                    <td className="border px-4 py-2">16,281</td>
                    <td className="border px-4 py-2">16,409</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">36</td>
                    <td className="border px-4 py-2">16,608</td>
                    <td className="border px-4 py-2">164</td>
                    <td className="border px-4 py-2">16,444</td>
                    <td className="border px-4 py-2">0</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            <div className="mt-6">
  {/* Table for medium and up */}
  <div className="hidden md:block overflow-x-auto">
    <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
      <thead>
      <tr className="bg-gray-100">
          <th className="border px-4 py-2 text-left">Month</th>
          <th className="border px-4 py-2 text-left">EMI (₹)</th>
          <th className="border px-4 py-2 text-left">Interest (₹)</th>
          <th className="border px-4 py-2 text-left">Principal (₹)</th>
          <th className="border px-4 py-2 text-left">Outstanding Balance (₹)</th>
        </tr>
      </thead>
      <tbody>
        {[
         
            ["1", "16,608", "5,000", "11,608", "4,88,392"],
            ["2", "16,608", "4,884", "11,724", "4,76,668"],
            ["3", "16,608", "4,767", "11,841", "4,64,827"],
            ["4", "16,608", "4,648", "11,960", "4,52,867"],
            ["5", "16,608", "4,529", "12,079", "4,40,788"],
            ["6", "16,608", "4,408", "12,200", "4,28,588"],
            ["7", "16,608", "4,286", "12,322", "4,16,266"],
            ["8", "16,608", "4,163", "12,445", "4,03,821"],
            ["9", "16,608", "4,038", "12,570", "3,91,251"],
            ["10", "16,608", "3,927", "12,681", "3,78,570"],
            ["11", "16,608", "3,785", "12,823", "3,65,747"],
            ["12", "16,608", "3,657", "12,951", "3,52,796"],
            ["13", "16,608", "3,527", "13,081", "3,39,715"],
            ["14", "16,608", "3,397", "13,211", "3,26,504"],
            ["15", "16,608", "3,265", "13,343", "3,13,161"],
            ["16", "16,608", "3,132", "13,476", "2,99,685"],
            ["17", "16,608", "2,997", "13,611", "2,86,074"],
            ["18", "16,608", "2,863", "13,745", "2,72,329"],
            ["19", "16,608", "2,727", "13,881", "2,58,448"],
            ["20", "16,608", "2,591", "14,017", "2,44,431"],
            ["21", "16,608", "2,455", "14,153", "2,30,278"],
            ["22", "16,608", "2,318", "14,290", "2,15,988"],
            ["23", "16,608", "2,181", "14,427", "2,01,561"],
            ["24", "16,608", "2,013", "14,595", "1,86,966"],
            ["25", "16,608", "1,870", "14,738", "1,72,228"],
            ["26", "16,608", "1,726", "14,882", "1,57,346"],
            ["27", "16,608", "1,573", "15,035", "1,42,311"],
            ["28", "16,608", "1,419", "15,189", "1,27,122"],
            ["29", "16,608", "1,271", "15,337", "1,11,785"],
            ["30", "16,608", "1,117", "15,491", "96,294"],
            ["31", "16,608", "963", "15,645", "80,649"],
            ["32", "16,608", "807", "15,801", "64,848"],
            ["33", "16,608", "649", "15,959", "48,889"],
            ["34", "16,608", "489", "16,119", "32,770"],
            ["35", "16,608", "327", "16,281", "16,489"],
            ["36", "16,608", "164", "16,444", "0"],
          // Add more rows as needed...
        ].map(([month, emi, interest, principal, balance], index) => (
          <tr key={index}>
                   <td className="border px-4 py-2">{month}</td>
            <td className="border px-4 py-2">{emi}</td>
            <td className="border px-4 py-2">{interest}</td>
            <td className="border px-4 py-2">{principal}</td>
            <td className="border px-4 py-2">{balance}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>

  {/* Card layout for small screens */}
  <div className="md:hidden space-y-4 mt-4">
    {[
      ["1", "16,608", "5,000", "11,608", "4,88,392"],
      ["2", "16,608", "4,884", "11,724", "4,76,668"],
      ["3", "16,608", "4,767", "11,841", "4,64,827"],
      ["4", "16,608", "4,648", "11,960", "4,52,867"],
      ["5", "16,608", "4,529", "12,079", "4,40,788"],
      ["6", "16,608", "4,408", "12,200", "4,28,588"],
      ["7", "16,608", "4,286", "12,322", "4,16,266"],
      ["8", "16,608", "4,163", "12,445", "4,03,821"],
      ["9", "16,608", "4,038", "12,570", "3,91,251"],
      ["10", "16,608", "3,927", "12,681", "3,78,570"],
      ["11", "16,608", "3,785", "12,823", "3,65,747"],
      ["12", "16,608", "3,657", "12,951", "3,52,796"],
      ["13", "16,608", "3,527", "13,081", "3,39,715"],
      ["14", "16,608", "3,397", "13,211", "3,26,504"],
      ["15", "16,608", "3,265", "13,343", "3,13,161"],
      ["16", "16,608", "3,132", "13,476", "2,99,685"],
      ["17", "16,608", "2,997", "13,611", "2,86,074"],
      ["18", "16,608", "2,863", "13,745", "2,72,329"],
      ["19", "16,608", "2,727", "13,881", "2,58,448"],
      ["20", "16,608", "2,591", "14,017", "2,44,431"],
      ["21", "16,608", "2,455", "14,153", "2,30,278"],
      ["22", "16,608", "2,318", "14,290", "2,15,988"],
      ["23", "16,608", "2,181", "14,427", "2,01,561"],
      ["24", "16,608", "2,013", "14,595", "1,86,966"],
      ["25", "16,608", "1,870", "14,738", "1,72,228"],
      ["26", "16,608", "1,726", "14,882", "1,57,346"],
      ["27", "16,608", "1,573", "15,035", "1,42,311"],
      ["28", "16,608", "1,419", "15,189", "1,27,122"],
      ["29", "16,608", "1,271", "15,337", "1,11,785"],
      ["30", "16,608", "1,117", "15,491", "96,294"],
      ["31", "16,608", "963", "15,645", "80,649"],
      ["32", "16,608", "807", "15,801", "64,848"],
      ["33", "16,608", "649", "15,959", "48,889"],
      ["34", "16,608", "489", "16,119", "32,770"],
      ["35", "16,608", "327", "16,281", "16,489"],
      ["36", "16,608", "164", "16,444", "0"],
    ].map(([month, emi, interest, principal, balance], index) => (
      <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
        <div><span className="font-semibold">Month:</span> {month}</div>
        <div><span className="font-semibold">EMI (₹):</span> {emi}</div>
        <div><span className="font-semibold">Interest (₹):</span> {interest}</div>
        <div><span className="font-semibold">Principal (₹):</span> {principal}</div>
        <div><span className="font-semibold">Outstanding Balance (₹):</span> {balance}</div>
      </div>
    ))}
  </div>
</div>

          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinMaster’s Personal Loan EMI Calculator
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <li><strong>Time-Saving & Accurate:</strong> Provides quick, error-free results in seconds.</li>
                <li><strong>Financial Planning:</strong> Helps you understand how much you’ll pay monthly and cumulatively.</li>
                <li><strong>Customizable Scenarios:</strong> Try different amounts, tenures, and rates to pick your best option.</li>
                <li><strong>Visual Clarity:</strong> Includes a month-by-month breakdown of interest vs. principal.</li>
                <li><strong>Zero Risk, No Login:</strong> Fully secure, no personal data required, and completely free to use.</li>
                <li><strong>Empowers Decision-Making:</strong> Helps choose EMI values that fit your income and lifestyle.</li>
              </ul>
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Factors That Affect Your Personal Loan EMI
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Several variables determine your EMI amount. Understanding these factors helps you negotiate better loan terms:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Loan Amount:</strong> A higher principal naturally increases your EMI unless extended over a longer tenure.</li>
              <li><strong>Interest Rate:</strong> Even a 0.5% difference in interest rates can significantly impact the total repayment.</li>
              <li><strong>Tenure:</strong> A longer loan duration reduces your EMI but increases your total interest payout.</li>
              <li><strong>Credit Score:</strong> Higher scores can qualify you for better rates, indirectly lowering EMI.</li>
              <li><strong>Lender Type:</strong> Banks, NBFCs, and fintech lenders offer different interest rate structures and processing charges.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Who Should Use a Personal Loan EMI Calculator?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Anyone considering a personal loan—regardless of the purpose—should use this calculator to make informed financial decisions. Whether you're a salaried individual, self-employed, or a professional planning a big purchase or consolidating debts, this tool can help.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              It’s especially useful for:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>First-time borrowers who need clarity on affordability</li>
              <li>Those comparing different loan offers</li>
              <li>Individuals aiming to avoid over-borrowing</li>
              <li>Anyone wanting to maintain a good credit score by planning realistic repayments</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Prepayment and Foreclosure Insights
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Understanding the impact of prepayments or early loan closures is critical. While the EMI calculator doesn’t factor this in directly, it gives a baseline that helps you estimate potential savings if you pay off your loan earlier than planned.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Use Cases: Common Scenarios
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <li><strong>Wedding Loan:</strong> For large events, plan EMI within your event budget.</li>
                <li><strong>Medical Emergencies:</strong> Helps balance urgent medical needs with manageable monthly outflow.</li>
                <li><strong>Home Renovation:</strong> See how extending tenure lowers EMI but raises interest.</li>
                <li><strong>Debt Consolidation:</strong> Use to evaluate if personal loans help reduce the burden from credit cards or informal borrowings.</li>
              </ul>
            </p>
          </div>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                        {/* Ad Banner */}
                      <div className="mt-4">
                        <div className="bg-white shadow-md rounded-lg overflow-hidden">
                          <div className="p-3 w-full h-auto rounded-md object-cover">
                            <AdBanner displayOrder="2" />
                          </div>
                        </div>
                      </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                {/* <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link> */}
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
   </>
  );
};

export default PersonalLoanEmiCalculator;
